import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { allCourses } from '../../../mocks/courses';
import CoursePaymentModal from '../../course/components/CoursePaymentModal';

interface Props {
  postCategory?: string;
}

const CATEGORY_MAP: Record<string, string[]> = {
  '面試技巧': ['faang-interview-sprint', 'english-interview-pro', 'ai-pm-bootcamp'],
  '職場經驗': ['resume-linkedin-pro', 'data-science-career', 'faang-interview-sprint'],
  'AI 趨勢':  ['faang-interview-sprint', 'ml-system-design', 'ai-pm-bootcamp'],
  '技術學習': ['ml-system-design', 'data-science-career', 'faang-interview-sprint'],
  '求職技巧': ['resume-linkedin-pro', 'faang-interview-sprint', 'ai-pm-bootcamp'],
};

const DEFAULT_IDS = ['faang-interview-sprint', 'ai-pm-bootcamp', 'resume-linkedin-pro'];

const BlogCourseCta = ({ postCategory }: Props) => {
  const navigate = useNavigate();
  const [modalCourse, setModalCourse] = useState<typeof allCourses[0] | null>(null);
  const [enrolledIds, setEnrolledIds] = useState<Set<string>>(new Set());

  const ids = (postCategory && CATEGORY_MAP[postCategory]) ? CATEGORY_MAP[postCategory] : DEFAULT_IDS;
  const courses = ids
    .map((id) => allCourses.find((c) => c.id === id))
    .filter(Boolean) as typeof allCourses;

  return (
    <>
      <div className="mt-10 pt-8 border-t border-slate-100">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-xl">
              <i className="ri-book-open-line text-amber-500 text-base" />
            </div>
            <div>
              <p className="text-sm font-bold text-[#0A1628]">把文章變成實力</p>
              <p className="text-xs text-slate-400">這些課程可以幫你系統深入這個方向</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/courses')}
            className="text-xs text-violet-600 font-semibold hover:text-violet-700 cursor-pointer whitespace-nowrap transition-colors flex items-center gap-1"
          >
            全部課程
            <i className="ri-arrow-right-line" />
          </button>
        </div>

        {/* Course Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {courses.map((course) => {
            const discountPct = Math.round((1 - course.price / course.originalPrice) * 100);
            const isEnrolled = enrolledIds.has(course.id);
            return (
              <div
                key={course.id}
                className="bg-slate-50 border border-slate-100 hover:border-violet-200 rounded-2xl overflow-hidden transition-all duration-200 group cursor-pointer"
                onClick={() => navigate(`/course/${course.id}`)}
              >
                {/* Gradient top strip */}
                <div className={`bg-gradient-to-br ${course.gradient} px-4 pt-4 pb-3`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg">
                      <i className={`${course.icon} text-white text-sm`} />
                    </div>
                    <span className={`${course.tagColor} text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap`}>
                      {course.tag}
                    </span>
                  </div>
                  <h4 className="text-white font-bold text-xs leading-snug line-clamp-2">{course.title}</h4>
                </div>

                {/* Body */}
                <div className="px-4 py-3">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="flex items-center gap-0.5 text-xs">
                      <i className="ri-star-fill text-amber-400 text-[11px]" />
                      <span className="font-bold text-[#0A1628]">{course.rating}</span>
                    </span>
                    <span className="text-[11px] text-slate-400">{course.students.toLocaleString()} 人</span>
                    <span className="text-[11px] text-slate-300">·</span>
                    <span className="text-[11px] text-slate-400">{course.duration}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-baseline gap-1">
                      <span className="text-base font-black text-[#0A1628]">${course.price}</span>
                      <span className="text-xs text-emerald-600 font-bold">-{discountPct}%</span>
                    </div>
                    {isEnrolled ? (
                      <span className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
                        <i className="ri-checkbox-circle-fill" />已報名
                      </span>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); setModalCourse(course); }}
                        className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-[11px] font-bold rounded-lg cursor-pointer whitespace-nowrap transition-colors active:scale-95"
                      >
                        立即購買
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {modalCourse && (
        <CoursePaymentModal
          course={modalCourse}
          onClose={() => setModalCourse(null)}
          onSuccess={() => {
            setEnrolledIds((prev) => new Set(prev).add(modalCourse.id));
            setModalCourse(null);
          }}
        />
      )}
    </>
  );
};

export default BlogCourseCta;
